import { useReducer, useState, useRef } from 'react'

import csrfHeaders from 'src/utils/csrfHeaders'
import filestackTransformAndUpload from 'src/utils/filestackTransformAndUpload'

const reducer = (state, action) => {
  switch (action.type) {
  case 'setFieldValue':
    return { ...state, [action.field]: action.value }
  case 'reset':
    return action.snapshot
  default:
    throw new Error('Invalid reducer action')
  }
}

const useLiveEdit = ({ config, filestack_api_key, s3_bucket, s3_region }) => {
  const [ state, dispatch ] = useReducer(reducer, config)
  const [ errors, setErrors ] = useState({})
  const [ loading, setLoading ] = useState(false)
  const [ snapshot, setSnapshot ] = useState(state)
  const [ saved, setSaved ] = useState(false)

  // use the existance of image upload props to deduce fundraiser ownership
  const canUploadAndSave = !!filestack_api_key

  const baseProps = id => ({ id, error: errors?.[id]?.[0] })

  const inputProps = id => ({
    ...baseProps(id),
    value: state[id],
    onChange (e) {
      dispatch({ type: 'setFieldValue', field: id, value: e.target.value })
    }
  })

  const booleanProps = id => ({
    ...baseProps(id),
    checked: state[id],
    onChange (e) {
      dispatch({ type: 'setFieldValue', field: id, value: e.target.checked })
    }
  })

  const client = useRef()
  if (canUploadAndSave && !client.current)
    client.current = filestack.init(filestack_api_key)

  const setBackgroundImage = file =>
    dispatch({
      type: 'setFieldValue',
      field: 'background_image',
      value: file.key,
    })

  const storeOptions = {
    location: 's3',
    path: '/media/images/live-display-backgrounds/',
    container: s3_bucket,
    region: s3_region,
    access: 'public',
  }

  const pickFile = () => {
    const nativeInput = document.createElement('input')
    nativeInput.type = 'file'
    nativeInput.accept = 'image/*'
    nativeInput.addEventListener('change', () => {
      const file = nativeInput.files[0]
      if (!file) return
      filestackTransformAndUpload(client.current, filestack_api_key, file, storeOptions, {
        TransformClass: top.FilestackTransform,
      }).then(result => { if (result?.key) setBackgroundImage(result) })
        .catch(err => console.error('File upload failed:', err))
    })
    nativeInput.click()
  }

  const handleSubmit = e => {
    e.preventDefault()
    if (loading || !canUploadAndSave) return

    setLoading(true)
    setErrors({})

    fetch(location.pathname, {
      method: 'PATCH',
      headers: csrfHeaders(),
      body: JSON.stringify({ live_display: state })
    }).then(response => {
      if (response.ok) {
        setSnapshot(state)
        setSaved(true)
        setTimeout(() => setSaved(false), 1000)
      } else
        response.json().then(json => setErrors(json)).catch(error => {
          console.error(error)
          setErrors({ base: [ 'There was a server error. Please try again.' ] })
        })
    }).catch(error => {
      console.error(error)
      setErrors({ base: [ 'There was a network error. Please try again.' ] })
    }).finally(() => {
      setLoading(false)
    })
  }

  const resetForm = () => {
    dispatch({ type: 'reset', snapshot })
    setErrors({})
  }

  return {
    state, dispatch, errors, inputProps, booleanProps, pickFile, handleSubmit,
    canUploadAndSave, snapshot, resetForm, saved,
  }
}

export default useLiveEdit
